/**
 * Custom Pop-up Handler
 * Manages pop-up display logic, timing, and cookie-based frequency control
 */

class CustomPopup {
  constructor(element) {
    this.popup = element;
    this.popupId = this.popup.dataset.popupId;
    this.delay = parseInt(this.popup.dataset.delay) * 1000 || 3000;
    this.frequency = this.popup.dataset.frequency || 'once_per_day';
    this.showOn = this.popup.dataset.showOn || 'all_pages';
    this.cookieDuration = parseInt(this.popup.dataset.cookieDuration) || 7;
    this.cookieName = `popup_${this.popupId}_closed`;

    this.closeButton = this.popup.querySelector('.custom-popup-close');
    this.overlay = this.popup;

    this.init();
  }

  init() {
    // Check if popup should be shown based on page type
    if (!this.shouldShowOnCurrentPage()) {
      return;
    }

    // Check if popup was already shown based on frequency setting
    if (!this.shouldShowBasedOnFrequency()) {
      return;
    }

    // Set up event listeners
    this.setupEventListeners();

    // Show popup after delay
    this.showAfterDelay();
  }

  shouldShowOnCurrentPage() {
    const pathname = window.location.pathname;

    switch (this.showOn) {
      case 'homepage':
        return pathname === '/' || pathname === '';

      case 'product_pages':
        return pathname.includes('/products/');

      case 'collection_pages':
        return pathname.includes('/collections/');

      case 'all_pages':
      default:
        return true;
    }
  }

  shouldShowBasedOnFrequency() {
    // Always show for testing purposes
    if (this.frequency === 'always') {
      return true;
    }

    // Check session storage for "once per session"
    if (this.frequency === 'once') {
      if (sessionStorage.getItem(this.cookieName)) {
        return false;
      }
      return true;
    }

    // Check cookie for time-based frequency
    const closedTimestamp = this.getCookie(this.cookieName);

    if (!closedTimestamp) {
      return true;
    }

    const now = Date.now();
    const timePassed = now - parseInt(closedTimestamp);

    // Calculate time threshold based on frequency
    let threshold;
    switch (this.frequency) {
      case 'once_per_day':
        threshold = 24 * 60 * 60 * 1000; // 24 hours
        break;
      case 'once_per_week':
        threshold = 7 * 24 * 60 * 60 * 1000; // 7 days
        break;
      default:
        threshold = this.cookieDuration * 24 * 60 * 60 * 1000;
    }

    return timePassed > threshold;
  }

  setupEventListeners() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Close on overlay click (outside popup)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('show')) {
        this.close();
      }
    });
  }

  showAfterDelay() {
    setTimeout(() => {
      this.show();
    }, this.delay);
  }

  show() {
    // Make popup visible (display: flex)
    this.popup.classList.add('active');

    // Trigger animation after a brief moment
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.popup.classList.add('show');
      });
    });

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Track popup shown
    this.trackShown();
  }

  close() {
    // Remove animation class
    this.popup.classList.remove('show');

    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.popup.classList.remove('active');
      document.body.style.overflow = '';
    }, 300);

    // Save closure information based on frequency
    if (this.frequency === 'once') {
      // For "once per session", use sessionStorage
      sessionStorage.setItem(this.cookieName, 'true');
    } else {
      // For time-based frequency, use cookie with timestamp
      this.setCookie(this.cookieName, Date.now().toString(), this.cookieDuration);
    }

    // Track popup closed
    this.trackClosed();
  }

  trackShown() {
    // Add analytics tracking if needed
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'popup_shown',
        popup_id: this.popupId
      });
    }
  }

  trackClosed() {
    // Add analytics tracking if needed
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'popup_closed',
        popup_id: this.popupId
      });
    }
  }

  // Cookie helper methods
  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }
}

// Initialize all popups on page load
document.addEventListener('DOMContentLoaded', () => {
  const popups = document.querySelectorAll('.custom-popup-overlay');

  popups.forEach(popup => {
    new CustomPopup(popup);
  });
});

// Also initialize on Shopify theme editor load
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const popup = event.target.querySelector('.custom-popup-overlay');
    if (popup) {
      new CustomPopup(popup);
    }
  });
}
